import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Navbar } from '../components/Navbar';
import { useScreenInit } from '../useScreenInit';
import { Star, Heart, Award, ShieldCheck } from 'lucide-react';
const VALUES = [{
  icon: <Star className="text-[#b22222]" size={32} data-id="element-179" />,
  title: 'Uncompromising Elegance',
  description: 'We believe every detail matters. Our curated collection reflects a standard of sophistication that transforms ordinary spaces into extraordinary experiences.'
}, {
  icon: <Heart className="text-[#b22222]" size={32} data-id="element-180" />,
  title: 'Passion for Service',
  description: 'Our team is dedicated to your vision. We approach every project with a personal touch, ensuring your celebration is as unique as your story.'
}, {
  icon: <Award className="text-[#b22222]" size={32} data-id="element-181" />,
  title: 'Premium Quality',
  description: 'From velvet chairs to crystal candelabras, we source only the finest materials to ensure your event looks and feels truly luxurious.'
}, {
  icon: <ShieldCheck className="text-[#b22222]" size={32} data-id="element-182" />,
  title: 'Seamless Execution',
  description: 'With over a decade of experience, we handle the logistics so you can focus on the moments that matter most. Reliability is our hallmark.'
}];
export function AboutUs() {
  useScreenInit();
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.8
  }} className="min-h-screen bg-white font-sans selection:bg-[#b22222] selection:text-white" data-id="element-183">
      <TopBar data-id="element-184" />
      <Navbar data-id="element-185" />

      <main data-id="element-186">
        {/* Page Hero */}
        <section className="relative h-[40vh] flex items-center justify-center bg-black overflow-hidden" data-id="element-187">
          <div className="absolute inset-0 bg-cover bg-center opacity-50" style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1519167758481-83f550bb49b3?q=80&w=2074&auto=format&fit=crop")'
        }} data-id="element-188" />
          <div className="relative text-center text-white z-10" data-id="element-189">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-[10px] uppercase tracking-[0.4em] font-bold mb-4 block" data-id="element-190">
              Defining Elegance Since 2015
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="font-serif text-5xl md:text-7xl italic" data-id="element-191">
              Our Story
            </motion.h1>
          </div>
        </section>

        {/* Narrative Section */}
        <section className="py-24" data-id="element-192">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-193">
            <div className="max-w-[1440px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-194">
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="relative" data-id="element-195">
                <div className="aspect-[4/5] overflow-hidden" data-id="element-196">
                  <img src="https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=2069&auto=format&fit=crop" alt="Founder" className="w-full h-full object-cover shadow-2xl" data-id="element-197" />
                </div>
                <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-[#b22222] p-8 flex items-center justify-center text-white text-center hidden md:flex" data-id="element-198">
                  <div data-id="element-199">
                    <span className="text-4xl font-serif italic block mb-1" data-id="element-200">
                      10+
                    </span>
                    <span className="text-[10px] uppercase tracking-widest font-bold" data-id="element-201">
                      Years of Excellence
                    </span>
                  </div>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 30
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="space-y-8" data-id="element-202">
                <span className="text-[12px] uppercase tracking-[0.3em] font-bold text-[#b22222] block" data-id="element-203">
                  The Journey
                </span>
                <h2 className="font-serif text-4xl md:text-5xl text-gray-900 leading-tight" data-id="element-204">
                  Crafting Unforgettable <br data-id="element-205" />
                  <span className="italic" data-id="element-206">Atmospheres</span>
                </h2>
                <div className="space-y-6 text-gray-600 text-lg leading-relaxed" data-id="element-207">
                  <p data-id="element-208">
                    At Chyne Designs Elegant Events & Rentals LLC, we believe
                    that every event is a canvas waiting to be painted with
                    style and sophistication. Our journey began in 2015 with a
                    simple vision: to provide a level of aesthetic excellence
                    that was missing in the event rental market.
                  </p>
                  <p data-id="element-209">
                    What started as a boutique collection of specialty linens
                    has grown into a comprehensive inventory of high-end
                    furniture, atmospheric lighting, and bespoke décor. We don't
                    just rent items; we curate experiences that linger in the
                    memories of your guests long after the last toast is made.
                  </p>
                  <p data-id="element-210">
                    Our meticulous selection process ensures that every piece in
                    our warehouse meets the highest standards of quality and
                    design. Whether you're planning an intimate private dinner
                    or a grand corporate gala, we bring the same level of
                    passion and precision to every project.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 bg-gray-50" data-id="element-211">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-212">
            <div className="max-w-[1440px] mx-auto px-6" data-id="element-213">
              <div className="text-center mb-20" data-id="element-214">
                <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-6" data-id="element-215">
                  Our Core Values
                </h2>
                <div className="w-20 h-[1px] bg-[#b22222] mx-auto" data-id="element-216" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-217">
                {VALUES.map((value, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="text-center space-y-6" data-id="element-218">
                    <div className="flex justify-center" data-id="element-219">{value.icon}</div>
                    <h3 className="text-xl font-serif text-gray-900" data-id="element-220">
                      {value.title}
                    </h3>
                    <p className="text-gray-500 leading-relaxed text-sm" data-id="element-221">
                      {value.description}
                    </p>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-24 bg-black text-white overflow-hidden" data-id="element-222">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-223">
            <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-224">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} data-id="element-225">
                <span className="text-[#b22222] text-6xl font-serif mb-8 block leading-none" data-id="element-226">
                  "
                </span>
                <p className="text-2xl md:text-3xl font-serif italic mb-10 leading-relaxed" data-id="element-227">
                  Chyne Designs transformed our wedding into a fairytale. Their
                  attention to detail and the sheer quality of their rentals
                  exceeded every expectation we had. Truly the gold standard in
                  event design.
                </p>
                <div className="space-y-1" data-id="element-228">
                  <p className="text-sm font-bold uppercase tracking-widest" data-id="element-229">
                    Alexandra & James
                  </p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest" data-id="element-230">
                    Private Estate Wedding, 2023
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24" data-id="element-231">
          <div className="mx-4 md:mx-[4.1rem]" data-id="element-232">
            <div className="max-w-[1440px] mx-auto px-6 text-center" data-id="element-233">
              <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-8" data-id="element-234">
                Ready to create magic?
              </h2>
              <p className="text-gray-500 text-lg mb-12 max-w-2xl mx-auto" data-id="element-235">
                Let's discuss how we can bring your vision to life with our
                premium rentals and expert styling.
              </p>
              <Link to="/contact" data-id="element-236">
                <button className="bg-black text-white px-12 py-5 text-xs font-bold uppercase tracking-widest hover:bg-[#b22222] transition-all duration-300 shadow-xl" data-id="element-237">
                  Book a Consultation
                </button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-white py-12 border-t border-gray-100" data-id="element-238">
        <div className="mx-4 md:mx-[4.1rem]" data-id="element-239">
          <div className="max-w-[1440px] mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-240">
            <div className="flex flex-col items-center md:items-start leading-none" data-id="element-241">
              <span className="font-script text-3xl text-black" data-id="element-242">
                Chyne Designs
              </span>
              <span className="text-[8px] uppercase tracking-[0.2em] font-bold text-gray-400 mt-1" data-id="element-243">
                Elegant Events & Rentals LLC
              </span>
            </div>
            <div className="flex gap-8 text-[11px] font-bold uppercase tracking-widest text-gray-500" data-id="element-244">
              <a href="#" className="hover:text-black transition-colors" data-id="element-245">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-black transition-colors" data-id="element-246">
                Terms of Service
              </a>
              <a href="#" className="hover:text-black transition-colors" data-id="element-247">
                Contact
              </a>
            </div>
            <p className="text-[11px] text-gray-400" data-id="element-248">
              © 2026 Chyne Designs Elegant Events & Rentals LLC. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </motion.div>;
}